import {
  PlusIcon,
  ReceiptTextIcon,
  SearchIcon,
  TriangleAlertIcon,
  TruckIcon,
} from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import {
  ActiveChallanFilterChips,
  ChallanFilterSheet,
  useChallanDirectionLabel,
  useChallanTypeLabel,
} from "@/features/challans/ChallanFilterSheet"
import {
  CHALLAN_PAGE_SIZE,
  useChallanFilters,
  useChallanList,
  type ChallanSortKey,
} from "@/features/challans/use-challan-list"
import { DraftsStrip } from "@/features/drafts/DraftsStrip"
/* `useStatusLabel` reads the status as a WORD, for the mobile card's
 * `aria-label` — a screen reader would otherwise hear the row as a number and
 * a date with no indication of what state the movement is in. It was not
 * imported until 2026-08-16 because the rows were not links and there was no
 * label to write. */
import { DocumentStatusBadge, useStatusLabel } from "@/features/invoices/DocumentStatusBadge"
import { FeatureOff } from "@/components/FeatureOff"
import { SortableHead } from "@/components/SortableHead"
import { SyncStatus } from "@/components/SyncStatus"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import type { ChallanListRow } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * The delivery-challan register (§17.7, Rule 55 CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS SCREEN IS FOR. Rule 55 requires a delivery challan whenever goods
 * move WITHOUT a tax invoice, and the owner confirmed that happens both ways in
 * this business today with no document at all — material out to the galvaniser,
 * customer material in for fabrication. This register is where those movements
 * become visible, and it answers two questions nothing else in the app can:
 *
 *   "What have I delivered and not billed?"   → the `openOnly` narrowing
 *   "What is still out that should be back?"  → the `overdueOnly` narrowing
 *
 * A CHALLAN IS NOT A TAX INVOICE. The amount column exists because Rule 55(1)
 * requires a taxable value on the document; none of it is output tax, none of it
 * reaches GSTR-3B, and nobody pays it. The column is headed `lbl.total` like the
 * other two registers and is never labelled GST, tax or amount due — the
 * subtitle says so in words, because a money column beside a status badge is
 * read as money owed unless it is told not to be.
 *
 * THE DIRECTION IS SHOWN ON EVERY ROW, FROM THE WIRE. `row.direction` is derived
 * server-side by `challanDirection(challanType)` — INWARD for `JOB_WORK_IN`
 * alone — and on an inward row the CUSTOMER is the consigner and this business
 * is the consignee. Rule 55(1) requires both parties named, so a screen implying
 * "sent to" against an inward row describes the opposite of what happened. It is
 * never recomputed here; a second copy is precisely what could disagree with the
 * first.
 *
 * OVERDUE IS SERVER-COMPUTED, WITH A `<` BOUNDARY. Goods due back TODAY are not
 * late. Recomputing `isOverdue` from `expectedReturnDate` against the browser
 * clock would reintroduce the machine-local timezone bug and — worse — make the
 * register disagree with the `overdueOnly` filter that produced its own rows.
 *
 * CANCELLED CHALLANS ARE HIDDEN BY DEFAULT and the SERVER does it
 * (`status: { not: CANCELLED }` when no status is sent) — the quotation/VOID
 * rule, not the invoice/CANCELLED one. The subtitle has to say so or a cancelled
 * challan looks deleted, and the status select is the only way back to it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ROWS ARE LINKS SINCE 2026-08-16, when the detail screen was finally given
 * a `<Route>`. They deliberately were not before: `patterns.challan` was declared
 * in paths.ts with nothing mounted behind it, so a tap fell through to the
 * catch-all and redirected Home — which reads as the app ignoring you, on every
 * row, and is worse than a row that plainly does nothing.
 *
 * The stretched link lives on the NUMBER cell (`after:absolute after:inset-0`)
 * and needs `relative` on the row: without it the overlay resolves against the
 * page and every click anywhere opens the last row. `BilledMark` carries
 * `relative z-10` so "View the bill" is not swallowed by that overlay.
 * ─────────────────────────────────────────────────────────────────────────────
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function ChallanListScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()
  /*
   * `challan_date` is a CALENDAR DAY (`YYYY-MM-DD`) and goes STRAIGHT here.
   * Never `new Date(row.date)` — that parses a calendar day as UTC midnight and
   * renders the 9th for the 10th on any browser west of Greenwich. The ISO
   * INSTANTS on the same row (`returnedAt`) go through `istDayOfInstant` first;
   * see {@link ReturnNote}.
   */
  const formatDay = useFormatDay()
  const statusLabel = useStatusLabel()

  const { filters, setFilters, clearFilters, activeCount } = useChallanFilters()
  const query = useChallanList(filters)

  const sortBy = (sort: ChallanSortKey | undefined, dir: "asc" | "desc" | undefined) => {
    setFilters({ sort, dir })
  }

  const rows = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const first = total === 0 ? 0 : filters.offset + 1
  const last = Math.min(filters.offset + rows.length, total)

  /*
   * §18.3 — A 404 HERE MEANS THE FEATURE IS OFF, NOT THAT SOMETHING IS MISSING.
   *
   * Every route in `apps/api/src/routes/challans.ts` sits behind
   * `requireFlag("challans")`, which runs BEFORE `requireCap` and answers 404
   * rather than 403 precisely so that "forbidden" cannot leak the existence of
   * unreleased work. The client half of that contract is this branch: it must
   * NOT render "you do not have permission", which is exactly what a leaked 403
   * would look like, and it must not render a retry alert either — trying again
   * cannot switch a flag on.
   *
   * There is deliberately NO `useFlag` hook and no `GET /flags` query behind
   * this. That endpoint is `settings.write`, which the OWNER does not hold, so
   * asking it would 403 for the one person using this register.
   */
  if (query.error instanceof ApiError && query.error.status === 404) {
    return <ChallansNotSwitchedOn />
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("nav.challans")}</h1>
          <p className="text-muted-foreground max-w-2xl text-sm">{t("challan.listSubtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* A challan moves under this screen in ways the screen did not cause:
            * the lorry comes back, or a bill is raised against it on the shop
            * computer and the status flips to Billed. */}
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />
          {can("challan.create") ? (
            <Button nativeButton={false} render={<Link to={routes.challanNew} />}>
              <PlusIcon />
              {t("btn.newChallan")}
            </Button>
          ) : null}
        </div>
      </header>

      {/*
        * ABOVE THE REGISTER, AND ABOVE THE FILTERS. An unfinished challan is a
        * to-do, not a record — it has no number, so no filter here can reach it
        * and no sort can order it among documents that do.
        *
        * Gated on `challan.create`, matching the endpoint: reading the register
        * does not entitle you to somebody's half-typed form.
        */}
      {can("challan.create") ? <DraftsStrip kind="CHALLAN" /> : null}

      <div className="flex flex-col gap-3">
        <ChallanFilterBar
          filters={filters}
          setFilters={setFilters}
          clearFilters={clearFilters}
          activeCount={activeCount}
        />
        {/* What is currently narrowing the list, removable one at a time. */}
        <ActiveChallanFilterChips filters={filters} setFilters={setFilters} />
      </div>

      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : query.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              {/* The challan icon the sidebar and the drafts strip already use —
                * one picture for one document kind. */}
              <TruckIcon />
            </EmptyMedia>
            <EmptyTitle>
              {activeCount > 0 ? t("list.noneMatch") : t("challan.emptyTitle")}
            </EmptyTitle>
            <EmptyDescription>
              {activeCount > 0 ? t("list.noneMatchHelp") : t("challan.emptyHelp")}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {activeCount > 0 ? (
              <Button variant="outline" onClick={clearFilters}>
                {t("list.clearFilters")}
              </Button>
            ) : can("challan.create") ? (
              <Button nativeButton={false} render={<Link to={routes.challanNew} />}>
                <PlusIcon />
                {t("btn.newChallan")}
              </Button>
            ) : null}
          </EmptyContent>
        </Empty>
      ) : (
        <>
          {/* Phone: one card per challan. Not a cut-down table — the same facts,
            * stacked, because this is the layout the owner actually reads. */}
          <Card className="overflow-hidden py-0 md:hidden">
            <ul className="flex flex-col">
              {rows.map((row) => (
                <li
                  key={row.id}
                  data-overdue={row.isOverdue ? "true" : undefined}
                  className={`border-b last:border-b-0 ${
                    row.isOverdue ? "bg-red-50/60 dark:bg-red-950/20" : ""
                  }`}
                >
                  <Link
                    to={routes.challan(row.id)}
                    aria-label={t("list.rowLabel", {
                      number: row.number,
                      customer:
                        row.customerName === "" ? t("lbl.customerUnknown") : row.customerName,
                      date: formatDay(row.date),
                      status: statusLabel(row.status, "challan"),
                    })}
                    className="hover:bg-accent/50 relative flex min-h-14 items-start gap-3 p-4 transition-colors"
                  >
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium tabular-nums">{row.number}</span>
                      <DocumentStatusBadge
                        status={row.status}
                        kind="challan"
                        className="text-xs"
                      />
                      <OverdueMark row={row} />
                    </span>
                    <span className="truncate text-sm">
                      {row.customerName === "" ? (
                        <span className="text-muted-foreground">{t("lbl.customerUnknown")}</span>
                      ) : (
                        row.customerName
                      )}
                    </span>
                    <MovementLine row={row} />
                    <span className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      <span className="tabular-nums">{formatDay(row.date)}</span>
                      <VehicleNote row={row} />
                    </span>
                    <ReturnNote row={row} />
                    <BilledMark row={row} />
                  </span>
                  <DeclaredValue row={row} />
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          {/* Desktop. */}
          <Card className="hidden overflow-hidden py-0 md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead column="number" label={t("lbl.number")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="date" label={t("lbl.date")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="customer" label={t("lbl.customer")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  {/*
                    * THE SORT KEY IS `type`, NOT `challanType`. The FILTER
                    * parameter is `challanType`; the SORT parameter is `type`,
                    * and `ChallanListQuerySchema` is `.strict()`, so getting
                    * this wrong is a 400 rather than an ignored parameter.
                    */}
                  <SortableHead column="type" label={t("challan.typeLabel")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="status" label={t("lbl.status")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="total" align="end" label={t("lbl.total")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-overdue={row.isOverdue ? "true" : undefined}
                    /* `relative` IS LOAD-BEARING. The number's stretched link
                     * (`after:absolute after:inset-0`) resolves against the
                     * nearest POSITIONED ancestor — without this that is the
                     * page, and a click anywhere on the screen opens the last
                     * row. */
                    className={`hover:bg-accent/50 relative cursor-pointer ${
                      row.isOverdue ? "bg-red-50/60 dark:bg-red-950/20" : ""
                    }`}
                  >
                    <TableCell className="font-medium tabular-nums">
                      <Link
                        to={routes.challan(row.id)}
                        className="after:absolute after:inset-0 hover:underline"
                      >
                        {row.number}
                      </Link>
                    </TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {formatDay(row.date)}
                    </TableCell>
                    <TableCell className="max-w-[22ch] truncate">
                      {row.customerName === "" ? (
                        <span className="text-muted-foreground">{t("lbl.customerUnknown")}</span>
                      ) : (
                        row.customerName
                      )}
                    </TableCell>
                    <TableCell className="max-w-[26ch]">
                      <MovementLine row={row} />
                      <VehicleNote row={row} className="text-muted-foreground text-xs" />
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <DocumentStatusBadge status={row.status} kind="challan" />
                        <OverdueMark row={row} />
                        <BilledMark row={row} />
                      </span>
                      <ReturnNote row={row} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <DeclaredValue row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Pager
            first={first}
            last={last}
            total={total}
            offset={filters.offset}
            onOffset={(offset) => {
              setFilters({ offset })
              window.scrollTo({ top: 0, behavior: "smooth" })
            }}
          />
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Row parts                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * WHY the goods moved, and WHICH WAY — the two facts Rule 55(1) turns into the
 * consigner and consignee blocks on the printed page.
 *
 * The direction is a word, never a colour and never an arrow alone: on the one
 * INWARD type the customer is the sender and this business the receiver, and a
 * register that lets that be inferred from an icon will be read the way the
 * other five rows read.
 */
function MovementLine({ row }: { row: ChallanListRow }): React.JSX.Element {
  const typeLabel = useChallanTypeLabel()
  const directionLabel = useChallanDirectionLabel()

  return (
    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
      <span>{typeLabel(row.challanType)}</span>
      <Badge
        variant="outline"
        className={
          /* Inward is the exception and is coloured as one, but the WORD is
           * what carries the meaning — §11.1, and this one is not decoration. */
          row.direction === "INWARD"
            ? "border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300"
            : "text-muted-foreground"
        }
      >
        {directionLabel(row.direction)}
      </Badge>
    </span>
  )
}

/**
 * The lorry, printed EXACTLY as the owner typed it.
 *
 * The "Vehicle" word lives in the translated label, never baked onto the value —
 * the value is what he wrote on the gate pass and nothing may decorate it. It is
 * on the row because `q` searches it: at the gate the paper is with the driver
 * and the lorry number is the only reference anyone has.
 */
function VehicleNote({
  row,
  className,
}: {
  row: ChallanListRow
  className?: string
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (row.vehicleNumber === null) return null

  return (
    <span className={className ?? "tabular-nums"}>
      {t("challan.vehicle", { number: row.vehicleNumber })}
    </span>
  )
}

/**
 * When the goods are due back, or when they came back.
 *
 * TWO DIFFERENT TIME SHAPES, one line apart. `expectedReturnDate` is a CALENDAR
 * DAY and goes straight to `formatDay`; `returnedAt` is an ISO INSTANT and must
 * go through `istDayOfInstant` first. Slicing an instant to ten characters takes
 * the UTC day and prints it as an IST one, so a lorry logged back at 01:00 IST
 * would report the previous day.
 */
function ReturnNote({ row }: { row: ChallanListRow }): React.JSX.Element | null {
  const { t } = useTranslation()
  const formatDay = useFormatDay()

  if (row.returnedAt !== null) {
    return (
      <span className="text-muted-foreground text-xs">
        {t("challan.returnedOn", { date: formatDay(istDayOfInstant(row.returnedAt)) })}
      </span>
    )
  }

  if (row.expectedReturnDate === null) return null

  return (
    <span
      className={
        row.isOverdue
          ? "text-xs font-medium text-red-700 dark:text-red-400"
          : "text-muted-foreground text-xs"
      }
    >
      {t("challan.dueBack", { date: formatDay(row.expectedReturnDate) })}
    </span>
  )
}

/**
 * §17.7's whole point, and it must be impossible to miss.
 *
 * A CHIP CARRYING THE WORD, not a tint. The register is read on a phone in
 * workshop daylight by someone who is not hunting for it, and "these goods
 * should have come back" is the one thing on this screen that costs money if it
 * is scrolled past. The row also carries a red wash and a `data-overdue`
 * attribute, but the word is what does the work.
 *
 * `isOverdue` COMES FROM THE SERVER. It is `OPEN && isReturnable &&
 * expectedReturnDate < today`, with a `<` boundary so goods due back today are
 * not yet late — the same boundary `challanListWhere` uses, so a row the
 * `overdueOnly` filter returns is exactly a row this marks.
 */
function OverdueMark({ row }: { row: ChallanListRow }): React.JSX.Element | null {
  const { t } = useTranslation()
  if (!row.isOverdue) return null

  return (
    <Badge
      variant="outline"
      /* Explicit classes, both themes, for the reason DocumentStatusBadge gives:
       * the shadcn CLI owns `badge.tsx` and rewrites it, so a custom variant
       * added there would be lost on the next regeneration. */
      className="gap-1 border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
    >
      <TriangleAlertIcon aria-hidden="true" />
      {t("challan.overdue")}
    </Badge>
  )
}

/**
 * The bill that was finally raised against this movement (§17.7).
 *
 * THE ONLY HYPERLINK ON THE ROW, because it is the only one with a mounted route
 * and a real record behind it. `linkedInvoiceId` is a foreign key; the vehicle
 * number and the ship-to address are free text and are never linked.
 */
function BilledMark({ row }: { row: ChallanListRow }): React.JSX.Element | null {
  const { t } = useTranslation()
  if (row.linkedInvoiceId === null) return null

  return (
    <Link
      to={routes.invoice(row.linkedInvoiceId)}
      /*
       * `relative z-10` PUTS THIS ABOVE THE ROW'S STRETCHED LINK. Since
       * 2026-08-16 the number cell carries `after:absolute after:inset-0`, which
       * covers the whole row — without lifting this one, "View the bill" would
       * be swallowed and open the CHALLAN instead of the bill it names.
       */
      className="text-muted-foreground hover:text-foreground relative z-10 inline-flex items-center gap-1 text-xs hover:underline"
    >
      <ReceiptTextIcon aria-hidden="true" className="size-3" />
      {t("btn.viewBill")}
    </Link>
  )
}

/**
 * The declared value of the goods in transit — NOT money anyone owes.
 *
 * Rule 55(1) requires a taxable value on a delivery challan, so the lines run
 * through the same engine an invoice's do and this figure is real. None of it is
 * output tax and none of it reaches GSTR-3B. It is `formatRupees` like every
 * other amount in the app, which is what puts the Indian digit grouping on it —
 * `₹1,45,600.00`, never `₹145,600.00`, and never `toLocaleString`.
 */
function DeclaredValue({ row }: { row: ChallanListRow }): React.JSX.Element {
  return (
    <span className="flex shrink-0 flex-col items-end">
      <span className="font-medium tabular-nums">{formatRupees(row.grandTotalPaise)}</span>
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* The flag is off (§18.3)                                                     */
/* -------------------------------------------------------------------------- */

/**
 * What the register looks like when `requireFlag("challans")` refuses.
 *
 * IT SAYS "NOT SWITCHED ON", NOT "NOT ALLOWED". §18.3 makes a disabled feature a
 * 404 rather than a 403 so that "forbidden" cannot leak the existence of
 * unreleased work; rendering a permission message here would put that leak back
 * on the screen by hand. It offers no retry either — pressing it again cannot
 * flip a flag, and a button that never works is how someone concludes the app is
 * broken rather than merely incomplete.
 */
function ChallansNotSwitchedOn(): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("nav.challans")}</h1>
      </header>
      {/*
        * ONE COMPONENT AND ONE PAIR OF STRINGS FOR EVERY FLAGGED MODULE —
        * `components/FeatureOff.tsx`, which replaced this screen's own
        * `challan.notAvailableTitle`/`Help`. Named from the NAV LABEL, so the
        * panel and the sidebar row it sits under say the same word.
        */}
      <FeatureOff module={t("nav.challans")} icon={<TruckIcon />} />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The toolbar                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The filters the API actually supports — and no others.
 *
 * THE SAME SHAPE AS THE OTHER TWO REGISTERS' TOOLBARS (§11.1): a number search,
 * the status, and everything else behind one "More filters" button. The three
 * registers sit next to each other in the sidebar and the owner moves between
 * them all day; a filter that lives in a different place on each is a filter he
 * has to hunt for twice.
 *
 * THE STATUS IS NO LONGER A NATIVE `<select>` (2026-08-16, at the owner's
 * request, for visual consistency with the rest of the UI). The reason the
 * native one was chosen is kept rather than deleted: on a phone it opened the
 * operating system's own full-height wheel — bigger than anything drawn in the
 * page, already familiar, and reliably hittable without the page having to draw
 * a 48px target. That target is now drawn by hand, as `` on every
 * `SelectItem` and on the trigger; the rem class rather than a pixel literal so
 * it grows with the large-text preference. DO NOT TIDY THAT CLASS AWAY — it is
 * the only thing keeping this control usable in a workshop. The full version of
 * this note is above `InvoiceFilterBar` in the bill register.
 *
 * THE CHALLAN TYPE IN THE SHEET IS STILL NATIVE. `ChallanFilterSheet` was not
 * part of that change, so this screen carries one of each on purpose — that is
 * not an oversight waiting to be cleaned up.
 */
function ChallanFilterBar({
  filters,
  setFilters,
  clearFilters,
  activeCount,
}: {
  filters: ReturnType<typeof useChallanFilters>["filters"]
  setFilters: ReturnType<typeof useChallanFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
}): React.JSX.Element {
  const { t } = useTranslation()

  /*
   * The four ChallanStatus values plus "no filter". Declared once and handed
   * BOTH to `<Select items={…}>` and to the items themselves: Base UI resolves
   * the trigger's text from that array, and without it the trigger would print
   * the raw value — `INVOICED` on screen, in English, on a Marathi phone, on the
   * register whose tests forbid exactly that.
   */
  const statusOptions = [
    /* Empty sends no status, which the SERVER reads as "everything but
     * CANCELLED". Choosing Cancelled is the only way to see those. */
    { value: "", label: t("list.anyStatus") },
    { value: "OPEN", label: t("status.open") },
    { value: "RETURNED", label: t("status.returned") },
    { value: "INVOICED", label: t("status.invoiced") },
    { value: "CANCELLED", label: t("status.cancelled") },
  ]

  return (
    <div className="flex flex-wrap items-end gap-3">
      {/*
        * SEARCH OVER THE CHALLAN'S OWN REFERENCES — `challan_number` and
        * `vehicle_number`. It is a SERVER search (`?q=`), so it covers the whole
        * book rather than the fifty rows on this page.
        *
        * The vehicle number is in there deliberately: at the gate the paper is
        * with the driver and the lorry number is the only reference anyone has.
        * It does NOT match the customer name — that lives in a text-backed JSON
        * column the server will not query inside (CLAUDE.md), which is what the
        * customer filter in the sheet is for.
        */}
      <ChallanSearchBox filters={filters} setFilters={setFilters} />

      <div className="flex min-w-44 flex-col gap-1.5">
        <Label htmlFor="challan-status">{t("list.statusFilter")}</Label>
        <Select
          items={statusOptions}
          value={filters.status ?? ""}
          onValueChange={(value) => {
            /*
             * "" MEANS NO FILTER, and here that is load-bearing twice over. The
             * list schema is `.strict()`, so `?status=` is a 400 rather than a
             * no-op; and no status at all is what makes the SERVER return
             * everything except CANCELLED, so an accidental empty parameter
             * would change which rows come back. Base UI hands the value over
             * directly — there is no `event.target` — and types it nullable, so
             * null folds into the same branch.
             */
            setFilters({ status: value === "" || value == null ? undefined : String(value) })
          }}
        >
          {/* The id goes on the TRIGGER, which is the button `<Label htmlFor>`
            * above labels — a <button> is a labelable element, so
            * `getByLabelText("Status")` still resolves this control. */}
          <SelectTrigger id="challan-status" className="w-full">
            {/* The same string as the "" item: Base UI reads an empty value as
              * NO value, so without this the trigger would show a greyed-out
              * placeholder where the native select showed "All". */}
            <SelectValue placeholder={t("list.anyStatus")} />
          </SelectTrigger>
          {/* `w-auto` because the popup otherwise matches the trigger exactly and
            * clips the longer labels; the OS wheel sized itself. */}
          <SelectContent className="w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]">
            {statusOptions.map((option) => (
              /* `data-value` carries the MACHINE value onto the option element.
               * Base UI puts no value attribute on it, and the test that checks
               * these are the CHALLAN states rather than the invoice or
               * quotation ones would otherwise be reduced to reading translated
               * labels — which proves nothing about what reaches the API. */
              <SelectItem
                key={option.value}
                value={option.value}
                data-value={option.value}
                className="pl-3"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/*
        * EVERYTHING ELSE LIVES BEHIND ONE BUTTON. Status stays out here because
        * it is the filter reached for most — the server hides CANCELLED unless
        * it is asked for, and this select is the only way to ask.
        */}
      <ChallanFilterSheet
        filters={filters}
        setFilters={setFilters}
        clearFilters={clearFilters}
        activeCount={activeCount}
      />
    </div>
  )
}

/**
 * The number-and-vehicle search.
 *
 * DEBOUNCED, and mirrored in local state rather than read straight from the URL.
 * Writing every keystroke to the URL fires a request per character and re-renders
 * the whole screen through the router, which on a phone makes the field feel
 * like it is dropping letters.
 */
function ChallanSearchBox({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useChallanFilters>["filters"]
  setFilters: ReturnType<typeof useChallanFilters>["setFilters"]
}): React.JSX.Element {
  const { t } = useTranslation()
  const [text, setText] = useState(filters.q ?? "")

  // Follow the URL when it changes elsewhere — back button, "clear filters".
  useEffect(() => {
    setText(filters.q ?? "")
  }, [filters.q])

  useEffect(() => {
    const current = filters.q ?? ""
    if (text === current) return
    const timer = window.setTimeout(() => {
      setFilters({ q: text.trim() === "" ? undefined : text.trim() })
    }, 300)
    return () => {
      window.clearTimeout(timer)
    }
  }, [text, filters.q, setFilters])

  return (
    <div className="flex min-w-56 flex-1 flex-col gap-1.5 sm:max-w-xs">
      <Label htmlFor="challan-search">{t("list.searchChallans")}</Label>
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        />
        <Input
          id="challan-search"
          type="search"
          className="pl-9 tabular-nums"
          placeholder={t("list.searchChallansPlaceholder")}
          value={text}
          onChange={(event) => {
            setText(event.target.value)
          }}
        />
      </div>
    </div>
  )
}

function Pager({
  first,
  last,
  total,
  offset,
  onOffset,
}: {
  first: number
  last: number
  total: number
  offset: number
  onOffset: (offset: number) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (total <= CHALLAN_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("list.showingRange", { first, last, total })}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={offset === 0}
          onClick={() => {
            onOffset(Math.max(0, offset - CHALLAN_PAGE_SIZE))
          }}
        >
          {t("btn.previous")}
        </Button>
        <Button
          variant="outline"
          disabled={last >= total}
          onClick={() => {
            onOffset(offset + CHALLAN_PAGE_SIZE)
          }}
        >
          {t("btn.next")}
        </Button>
      </div>
    </div>
  )
}

function ListSkeleton(): React.JSX.Element {
  return (
    <Card className="overflow-hidden py-0">
      <ul className="flex flex-col">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-44" />
            </div>
            <Skeleton className="h-4 w-24" />
          </li>
        ))}
      </ul>
    </Card>
  )
}
